import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../../utils/api';
import { localeCourante } from '../../i18n';
import FenetreRequete, { BadgeStatut, BadgeUrgent, BoutonRequete, styleChamp, styleEtiquette } from './FenetreRequete';
import { ChoixDestinataires, ChoixPhotos } from './ChampsRequete';
import {
  changementsModification, entreesVisibles, idActif, motifValide, nomActif, nomCategorie, typeActif,
} from './requetesLogique';
import {
  commenterRequete, creerReparation, fermerRequete, lireConfigRequetes, lireRequete, messageErreur,
  modifierRequete, rouvrirRequete,
} from './requetesApi';

const TYPES_REPARATION = [
  ['reparation_mineure', 'reparations.reparationMineureDelai48h'],
  ['reparation_majeure', 'reparations.reparationMajeureImmobilisation'],
  ['entretien_preventif', 'reparations.entretienPreventifVidangeFiltres'],
  ['inspection_mecanique', 'reparations.inspectionMecaniquePepVignette'],
  ['autre', 'reparations.autre'],
];

export const formaterDateHeure = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(localeCourante(), { dateStyle: 'medium', timeStyle: 'short' });
};

/** Fiche d'une requête : fil complet, commentaire, et (officier) modifier / fermer / rouvrir. */
const RequeteDetailModal = ({ tenantSlug, user, requeteId, droits = {}, focusCommentaire, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [requete, setRequete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [mode, setMode] = useState(null); // null | 'modifier' | 'fermer' | 'rouvrir'
  const [texte, setTexte] = useState('');
  const [photos, setPhotos] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const zoneCommentaire = useRef(null);

  const charger = useCallback(async () => {
    try {
      const r = await lireRequete(tenantSlug, user?.id, requeteId);
      const cfg = await lireConfigRequetes(tenantSlug, user?.id, typeActif(r)).catch(() => null);
      setRequete(r);
      setCategories(cfg?.categories || []);
    } catch (e) {
      setErreur(messageErreur(e, t));
    }
  }, [tenantSlug, user?.id, requeteId, t]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (focusCommentaire && requete && zoneCommentaire.current) zoneCommentaire.current.focus();
  }, [focusCommentaire, requete]);
  useEffect(() => {
    if (mode !== 'modifier' || users.length) return;
    apiGet(tenantSlug, '/users').then((us) => setUsers(Array.isArray(us) ? us : us?.items || [])).catch(() => {});
    apiGet(tenantSlug, '/contact-emails').then((c) => setContacts(Array.isArray(c) ? c : c?.items || [])).catch(() => {});
  }, [mode, tenantSlug, users.length]);

  const executer = async (action) => {
    setEnvoi(true);
    setErreur('');
    try {
      const r = await action();
      if (r) setRequete(r);
      setMode(null);
      if (onChanged) onChanged(r);
      return true;
    } catch (e) {
      setErreur(messageErreur(e, t));
      return false;
    } finally {
      setEnvoi(false);
    }
  };

  const commenter = async () => {
    if (!texte.trim() && !photos.length) return;
    const ok = await executer(() => commenterRequete(tenantSlug, requeteId, { texte: texte.trim(), photos_base64: photos }));
    if (ok) { setTexte(''); setPhotos([]); }
  };

  if (!requete) {
    return (
      <FenetreRequete titre={t('requetes.requete')} onClose={onClose}>
        <p style={{ color: erreur ? '#b91c1c' : '#64748b' }}>{erreur || t('requetes.chargement')}</p>
      </FenetreRequete>
    );
  }

  const ouvert = requete.statut === 'ouvert';
  const fil = entreesVisibles(requete.fil);
  return (
    <FenetreRequete
      titre={`${t('requetes.requeteNumero', { numero: requete.numero })} — ${nomActif(requete)}`}
      sousTitre={`${t(`requetes.typeActif.${typeActif(requete)}`)} · ${nomCategorie(categories, requete.categorie_id)} · ${t(`requetes.source.${requete.source || 'manuelle'}`)}`}
      badges={(
        <>
          {requete.priorite === 'urgente' && <BadgeUrgent texte={t('requetes.urgentMaj')} />}
          <BadgeStatut ouvert={ouvert} texte={t(`requetes.statut.${requete.statut}`)} />
          {requete.met_hors_service && ouvert && <BadgeUrgent texte={t('requetes.horsService')} />}
        </>
      )}
      onClose={onClose}
      largeur="820px"
      pied={droits.modifier && !mode ? (
        <>
          {ouvert && <BoutonRequete contour couleur="#2563eb" onClick={() => setMode('modifier')}>{t('requetes.modifier')}</BoutonRequete>}
          {ouvert && <BoutonRequete couleur="#166534" onClick={() => setMode('fermer')}>{t('requetes.fermer')}</BoutonRequete>}
          {!ouvert && <BoutonRequete couleur="#b45309" onClick={() => setMode('rouvrir')}>{t('requetes.rouvrir')}</BoutonRequete>}
        </>
      ) : null}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px 16px', fontSize: '0.88rem', marginBottom: '14px' }}>
        <div><strong>{t('requetes.ouverteLe')}</strong><br />{formaterDateHeure(requete.created_at)}</div>
        <div><strong>{t('requetes.ouvertePar')}</strong><br />{requete.cree_par_nom}</div>
        <div><strong>{t('requetes.cout')}</strong><br />{requete.cout != null ? `${Number(requete.cout).toFixed(2)} $` : t('requetes.aucun')}</div>
        <div><strong>{t('requetes.destinataires')}</strong><br />
          {t('requetes.nbDestinataires', { nb: (requete.destinataires_ids || []).length + (requete.destinataires_emails || []).length })}
        </div>
      </div>

      <h3 style={{ fontSize: '0.95rem', margin: '0 0 6px', color: '#111827' }}>{t('requetes.descriptionEtCommentaires')}</h3>
      <div style={{ borderTop: '1px solid #e5e7eb' }}>
        {fil.map((e) => (
          <div key={e.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: '0.82rem' }}>
              <span style={{ color: '#dc2626', fontWeight: 600 }}>{formaterDateHeure(e.date)}</span>{' '}
              <strong>{e.auteur_nom}</strong>{' '}
              <span style={{ color: '#64748b' }}>{t(`requetes.fil.${e.type}`)}</span>
            </div>
            {e.texte && <div style={{ whiteSpace: 'pre-wrap', marginTop: '4px', color: '#1f2937' }}>{e.texte}</div>}
            {(e.photos_urls || []).length > 0 && (
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                {e.photos_urls.map((u) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer">
                    <img src={u} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {ouvert && droits.commenter && !mode && (
        <div style={{ marginTop: '14px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <label style={styleEtiquette} htmlFor="rq-commentaire">{t('requetes.ajouterCommentaire')}</label>
          <textarea id="rq-commentaire" ref={zoneCommentaire} value={texte} onChange={(e) => setTexte(e.target.value)}
            style={{ ...styleChamp, minHeight: '70px', marginBottom: '8px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <ChoixPhotos photos={photos} onChange={setPhotos} />
            <BoutonRequete onClick={commenter} disabled={envoi || (!texte.trim() && !photos.length)}>
              {envoi ? t('requetes.envoiEnCours') : t('requetes.publier')}
            </BoutonRequete>
          </div>
        </div>
      )}

      {mode === 'modifier' && (
        <PanneauModifier requete={requete} categories={categories} users={users} contacts={contacts} envoi={envoi}
          onAnnuler={() => setMode(null)}
          onValider={(corps) => executer(() => modifierRequete(tenantSlug, requeteId, corps))} />
      )}
      {mode === 'fermer' && (
        <PanneauFermer requete={requete} envoi={envoi} onAnnuler={() => setMode(null)}
          onValider={(motif, reparation) => executer(async () => {
            // Équipement / ÉPI : le serveur crée l'entrée de travaux TERMINÉS (historique de
            // maintenance, réparation d'ÉPI) sans changer l'état de l'actif.
            if (typeActif(requete) !== 'vehicule') {
              return fermerRequete(tenantSlug, requeteId, { motif, travaux: reparation || null });
            }
            let reparationId = null;
            if (reparation) {
              const rep = await creerReparation(tenantSlug, idActif(requete), reparation);
              reparationId = rep?.reparation?.id || null;
            }
            return fermerRequete(tenantSlug, requeteId, { motif, reparation_id: reparationId });
          })} />
      )}
      {mode === 'rouvrir' && (
        <PanneauMotif titre={t('requetes.rouvrirLaRequete')} bouton={t('requetes.rouvrir')} couleur="#b45309" envoi={envoi}
          onAnnuler={() => setMode(null)}
          onValider={(motif) => executer(() => rouvrirRequete(tenantSlug, requeteId, { motif }))} />
      )}
      {erreur && <p role="alert" style={{ color: '#b91c1c', marginTop: '10px' }}>{erreur}</p>}
    </FenetreRequete>
  );
};

const cadrePanneau = { marginTop: '14px', padding: '14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', display: 'grid', gap: '12px' };

const ChampMotif = ({ motif, setMotif }) => {
  const { t } = useTranslation();
  return (
    <div>
      <label style={styleEtiquette} htmlFor="rq-motif">{t('requetes.motif')} *</label>
      <textarea id="rq-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
        placeholder={t('requetes.motifAide')} style={{ ...styleChamp, minHeight: '60px' }} />
    </div>
  );
};

const PanneauMotif = ({ titre, bouton, couleur, envoi, onAnnuler, onValider }) => {
  const { t } = useTranslation();
  const [motif, setMotif] = useState('');
  return (
    <div style={cadrePanneau}>
      <strong>{titre}</strong>
      <ChampMotif motif={motif} setMotif={setMotif} />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <BoutonRequete contour couleur="#64748b" onClick={onAnnuler}>{t('requetes.annuler')}</BoutonRequete>
        <BoutonRequete couleur={couleur} disabled={envoi || !motifValide(motif)} onClick={() => onValider(motif.trim())}>{bouton}</BoutonRequete>
      </div>
    </div>
  );
};

const PanneauModifier = ({ requete, categories, users, contacts, envoi, onAnnuler, onValider }) => {
  const { t } = useTranslation();
  const [f, setF] = useState({
    categorie_id: requete.categorie_id, description: requete.description, priorite: requete.priorite,
    cout: requete.cout ?? '', met_hors_service: !!requete.met_hors_service,
    destinataires_ids: requete.destinataires_ids || [], destinataires_emails: requete.destinataires_emails || [],
  });
  const [motif, setMotif] = useState('');
  const [aviser, setAviser] = useState(true);
  const [message, setMessage] = useState('');
  const valider = () => {
    const champs = changementsModification(requete, f);
    if (!Object.keys(champs).length) { setMessage(t('requetes.aucunChangement')); return; }
    onValider({ ...champs, motif: motif.trim(), aviser });
  };
  return (
    <div style={cadrePanneau}>
      <strong>{t('requetes.modifierLaRequete')}</strong>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
        <div>
          <label style={styleEtiquette} htmlFor="rq-m-cat">{t('requetes.categorie')}</label>
          <select id="rq-m-cat" value={f.categorie_id} onChange={(e) => setF({ ...f, categorie_id: e.target.value })} style={styleChamp}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        </div>
        <div>
          <label style={styleEtiquette} htmlFor="rq-m-prio">{t('requetes.prioriteLibelle')}</label>
          <select id="rq-m-prio" value={f.priorite} onChange={(e) => setF({ ...f, priorite: e.target.value })} style={styleChamp}>
            <option value="routine">{t('requetes.priorite.routine')}</option>
            <option value="urgente">{t('requetes.priorite.urgente')}</option>
          </select>
        </div>
        <div>
          <label style={styleEtiquette} htmlFor="rq-m-cout">{t('requetes.cout')}</label>
          <input id="rq-m-cout" type="number" step="0.01" min="0" value={f.cout} onChange={(e) => setF({ ...f, cout: e.target.value })} style={styleChamp} />
        </div>
      </div>
      <div>
        <label style={styleEtiquette} htmlFor="rq-m-desc">{t('requetes.description')}</label>
        <textarea id="rq-m-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} style={{ ...styleChamp, minHeight: '60px' }} />
      </div>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input type="checkbox" checked={f.met_hors_service} onChange={(e) => setF({ ...f, met_hors_service: e.target.checked })} />
        <strong style={{ color: '#991b1b' }}>{t('requetes.metHorsService')}</strong>
      </label>
      <ChoixDestinataires users={users} contacts={contacts} ids={f.destinataires_ids} emails={f.destinataires_emails}
        auteurId={requete.cree_par} onChange={(i, e) => setF({ ...f, destinataires_ids: i, destinataires_emails: e })} />
      <ChampMotif motif={motif} setMotif={setMotif} />
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.9rem' }}>
        <input type="checkbox" checked={aviser} onChange={(e) => setAviser(e.target.checked)} />
        {t('requetes.aviserDestinataires')}
      </label>
      {message && <p style={{ margin: 0, color: '#92400e' }}>{message}</p>}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <BoutonRequete contour couleur="#64748b" onClick={onAnnuler}>{t('requetes.annuler')}</BoutonRequete>
        <BoutonRequete couleur="#2563eb" disabled={envoi || !motifValide(motif)} onClick={valider}>{t('requetes.enregistrer')}</BoutonRequete>
      </div>
    </div>
  );
};

const PanneauFermer = ({ requete, envoi, onAnnuler, onValider }) => {
  const { t } = useTranslation();
  const estVehicule = typeActif(requete) === 'vehicule';
  // Un point d'eau n'a pas de journal de travaux : la raison de la fermeture reste au fil.
  const aJournalTravaux = typeActif(requete) !== 'point_eau';
  const [motif, setMotif] = useState('');
  const [avecReparation, setAvecReparation] = useState(false);
  const [rep, setRep] = useState({
    date_reparation: new Date().toISOString().slice(0, 10), type_intervention: 'reparation_mineure',
    description: `${t('requetes.requeteNumero', { numero: requete.numero })} : ${requete.description}`,
    cout: requete.cout ?? '', fournisseur: '', numero_facture: '', kilometrage_actuel: '',
  });
  const valider = () => onValider(motif.trim(), avecReparation ? (estVehicule ? {
    ...rep,
    cout: rep.cout === '' ? null : Number(rep.cout),
    kilometrage_actuel: rep.kilometrage_actuel === '' ? null : Number(rep.kilometrage_actuel),
    fournisseur: rep.fournisseur || null, numero_facture: rep.numero_facture || null,
  } : {
    date: rep.date_reparation, description: rep.description, cout: rep.cout === '' ? null : Number(rep.cout),
    fournisseur: rep.fournisseur || null, numero_facture: rep.numero_facture || null,
  }) : null);
  const champ = (cle) => (e) => setRep({ ...rep, [cle]: e.target.value });
  return (
    <div style={cadrePanneau}>
      <strong>{t('requetes.fermerLaRequete')}</strong>
      <ChampMotif motif={motif} setMotif={setMotif} />
      {aJournalTravaux && (
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="checkbox" checked={avecReparation} onChange={(e) => setAvecReparation(e.target.checked)} />
          <strong>{t(`requetes.creerEntreeTravaux.${typeActif(requete)}`)}</strong>
        </label>
      )}
      {avecReparation && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px' }}>
          {estVehicule && (
            <div>
              <label style={styleEtiquette} htmlFor="rq-r-type">{t('reparations.typeDIntervention')}</label>
              <select id="rq-r-type" value={rep.type_intervention} onChange={champ('type_intervention')} style={styleChamp}>
                {TYPES_REPARATION.map(([v, cle]) => <option key={v} value={v}>{t(cle)}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={styleEtiquette} htmlFor="rq-r-date">{t('reparations.dateDeReparation')}</label>
            <input id="rq-r-date" type="date" value={rep.date_reparation} onChange={champ('date_reparation')} style={styleChamp} />
          </div>
          <div>
            <label style={styleEtiquette} htmlFor="rq-r-cout">{t('reparations.cout')}</label>
            <input id="rq-r-cout" type="number" step="0.01" min="0" value={rep.cout} onChange={champ('cout')} style={styleChamp} />
          </div>
          <div>
            <label style={styleEtiquette} htmlFor="rq-r-garage">{t('reparations.fournisseurGarage')}</label>
            <input id="rq-r-garage" value={rep.fournisseur} onChange={champ('fournisseur')} style={styleChamp} />
          </div>
          <div>
            <label style={styleEtiquette} htmlFor="rq-r-facture">{t('reparations.nFacture')}</label>
            <input id="rq-r-facture" value={rep.numero_facture} onChange={champ('numero_facture')} style={styleChamp} />
          </div>
          {estVehicule && (
            <div>
              <label style={styleEtiquette} htmlFor="rq-r-km">{t('reparations.kilometrageActuel')}</label>
              <input id="rq-r-km" type="number" value={rep.kilometrage_actuel} onChange={champ('kilometrage_actuel')} style={styleChamp} />
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={styleEtiquette} htmlFor="rq-r-desc">{t('reparations.description')}</label>
            <textarea id="rq-r-desc" value={rep.description} onChange={champ('description')} style={{ ...styleChamp, minHeight: '50px' }} />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <BoutonRequete contour couleur="#64748b" onClick={onAnnuler}>{t('requetes.annuler')}</BoutonRequete>
        <BoutonRequete couleur="#166534" disabled={envoi || !motifValide(motif) || (avecReparation && !rep.description.trim())}
          onClick={valider}>{t('requetes.fermer')}</BoutonRequete>
      </div>
    </div>
  );
};

export default RequeteDetailModal;
